import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './AiAssistantFab.css';

const STORAGE_KEY = 'atozasAiFabPos';
const SIZE = 58;
const MARGIN = 16;
const DRAG_THRESHOLD = 6;

const defaultPos = () => ({
  x: Math.max(MARGIN, window.innerWidth - SIZE - MARGIN),
  y: Math.max(MARGIN, window.innerHeight - SIZE - MARGIN - 8),
});

const clampPos = (x, y) => {
  const maxX = Math.max(MARGIN, window.innerWidth - SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - SIZE - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, x)),
    y: Math.min(maxY, Math.max(MARGIN, y)),
  };
};

const loadPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPos();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return defaultPos();
    return clampPos(parsed.x, parsed.y);
  } catch (_) {
    return defaultPos();
  }
};

/**
 * AiAssistantFab
 * Fixed, draggable floating AI Assistant button shown on every authenticated page.
 * Position is persisted in localStorage. Click opens /ai-assistant (ignored after a drag).
 */
const AiAssistantFab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const btnRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const [pos, setPos] = useState(() =>
    typeof window !== 'undefined' ? loadPos() : { x: 0, y: 0 }
  );
  const [dragging, setDragging] = useState(false);

  // Keep FAB on-screen when the viewport resizes.
  useEffect(() => {
    const onResize = () => setPos((prev) => clampPos(prev.x, prev.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      /* ignore quota / private mode */
    }
  }, []);

  const onPointerDown = (e) => {
    // Left mouse / touch / pen only
    if (e.button != null && e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    dragRef.current = {
      startX,
      startY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* older browsers */
    }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }

    const next = clampPos(drag.originX + dx, drag.originY + dy);
    setPos(next);
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    if (!drag || (e.pointerId != null && drag.pointerId !== e.pointerId)) return;

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {
      /* ignore */
    }

    if (drag.moved) {
      suppressClickRef.current = true;
      setPos((prev) => {
        const next = clampPos(prev.x, prev.y);
        persist(next);
        return next;
      });
    }
    setDragging(false);
    dragRef.current = null;
  };

  const onClick = (e) => {
    // Suppress navigation if this gesture was a drag.
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
      return;
    }
    if (location.pathname === '/ai-assistant') return;
    navigate('/ai-assistant');
  };

  // Hide on the AI Assistant page itself to avoid covering the composer.
  if (location.pathname === '/ai-assistant') return null;

  const isLeft = typeof window !== 'undefined' && pos.x < window.innerWidth / 2;

  return (
    <button
      ref={btnRef}
      type="button"
      className={`aia-fab ${dragging ? 'is-dragging' : ''} ${isLeft ? 'is-left' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      aria-label="Open AI Assistant"
      title="AI Assistant — drag to move"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={onClick}
    >
      <svg className="aia-fab-icon" viewBox="0 0 64 64" aria-hidden="true">
        <defs>
          <linearGradient id="aiaFabGrad" x1="8" y1="56" x2="56" y2="8" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#2F6BFF" />
            <stop offset="55%" stopColor="#7B3FF2" />
            <stop offset="100%" stopColor="#C026D3" />
          </linearGradient>
        </defs>
        {/* Outer ring with gap at top-right */}
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="url(#aiaFabGrad)"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeDasharray="118 34"
          strokeDashoffset="28"
          transform="rotate(-25 32 32)"
        />
        {/* Chat bubble */}
        <path
          fill="url(#aiaFabGrad)"
          d="M32 18.5c-7.6 0-13.8 5.4-13.8 12.1 0 4.2 2.4 7.9 6.2 10.1v5.2l5.4-3.1c.7.1 1.4.2 2.2.2 7.6 0 13.8-5.4 13.8-12.1S39.6 18.5 32 18.5z"
        />
        {/* Typing dots */}
        <circle cx="25.2" cy="30.6" r="1.85" fill="#fff" />
        <circle cx="32" cy="30.6" r="1.85" fill="#fff" />
        <circle cx="38.8" cy="30.6" r="1.85" fill="#fff" />
        {/* Sparkle in ring gap */}
        <path
          fill="url(#aiaFabGrad)"
          d="M48.2 12.2l1.55 4.35 4.35 1.55-4.35 1.55-1.55 4.35-1.55-4.35-4.35-1.55 4.35-1.55z"
        />
      </svg>
      <span className="aia-fab-tooltip">AI Assistant</span>
    </button>
  );
};

export default AiAssistantFab;
